/**
 * Centralized Font Configuration
 * Single source of truth for all fonts in the application
 *
 * Usage:
 * - CSS files: use var(--font-ui-primary), var(--font-chart-symbols), etc.
 * - TSX/TS files: import { FONTS } from '@/config/fonts'
 */

// =============================================================================
// FONT DEFINITIONS
// =============================================================================

export const FONTS = {
  // UI fonts (React components, Chakra UI)
  ui: {
    primary: "'Inter', system-ui, sans-serif",
    heading: "'Inter', system-ui, sans-serif",
    mono: "'SF Mono', 'Fira Code', monospace",
  },

  // Chart fonts (SVG rendering, D3)
  chart: {
    primary: "'Inter', Arial, sans-serif",
    symbols: "HamburgSymbols, Arial, sans-serif",
  },
} as const;

// =============================================================================
// CSS VARIABLE NAMES (for reference in CSS files)
// =============================================================================

export const CSS_FONT_VARS = {
  uiPrimary: '--font-ui-primary',
  uiHeading: '--font-ui-heading',
  uiMono: '--font-ui-mono',
  chartPrimary: '--font-chart-primary',
  chartSymbols: '--font-chart-symbols',
} as const;

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

/** Get UI font by type */
export const getUIFont = (type: keyof typeof FONTS.ui): string => FONTS.ui[type];

/** Get Chart font by type */
export const getChartFont = (type: keyof typeof FONTS.chart): string => FONTS.chart[type];

/** Get CSS variable reference for use in inline styles */
export const getCSSVar = (varName: keyof typeof CSS_FONT_VARS): string =>
  `var(${CSS_FONT_VARS[varName]})`;

// =============================================================================
// TYPE EXPORTS
// =============================================================================

export type UIFontType = keyof typeof FONTS.ui;
export type ChartFontType = keyof typeof FONTS.chart;
